import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { DiagnosticReport, Observation, Specimen, Practitioner, Bundle, Reference } from 'fhir/r4';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { ChipsDirective } from '../chips.directive';
import { NaPipe } from '../shared/na.pipe';
import { DetailActionButton, DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { CodeableConcept2Pipe } from '../shared/codeable-concept2.pipe';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { forkJoin, of, Observable, switchMap, map, catchError } from 'rxjs';
import { DiagnosticReportDetailsComponent } from './diagnostic-report-details/diagnostic-report-details.component';

@Component({
    selector: 'app-diagnostic-report',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        MatTableModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatDividerModule,
        DatePipe,
        TitleCasePipe,
        EmptyStateComponent,
        TableHeaderComponent,
        ChipsDirective,

        NaPipe,
        CodeableConcept2Pipe,
        ReferenceDisplayDirective
    ],
    providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe],
    templateUrl: './diagnostic-report.component.html',
    styleUrls: ['../patient-observation/patient-observation.component.scss', './diagnostic-report.component.scss']
})
export class DiagnosticReportComponent extends AdmittedPatientsComponent implements OnInit, OnDestroy {
    // Override table columns - will be set dynamically in ngOnInit
    override displayedColumns: string[] = [];

    // Filters
    diagnosticReportFilters = new Map<string, string[]>([
        ['status', ['final', 'preliminary', 'amended', 'corrected', 'appended', 'cancelled', 'entered-in-error']],
        ['category', ['LAB', 'RAD', 'PATH', 'MB', 'HM', 'CT', 'NMR', 'NMS', 'RUS', 'OUS']]
    ]);

    // Details builder for dialog
    diagnosticReportDetailsBuilder: DetailsBuilderObject = {
        resourceName: 'Diagnostic Report',
        resourceIcon: 'science',
        specialHeader: {
            strongSectionKey: 'code',
            iconSectionKeys: ['status', 'category'],
            contentSectionKeys: ['issued', 'subject']
        },
        groups: [
            {
                groupName: 'Identification',
                groupIcon: 'badge',
                groupKeys: ['status', 'category', 'code', 'issued', 'effectiveDateTime']
            },
            {
                groupName: 'Context',
                groupIcon: 'person',
                groupKeys: ['subject', 'encounter']
            },
            {
                groupName: 'Results',
                groupIcon: 'analytics',
                groupKeys: [] // Dynamically populated
            },
            {
                groupName: 'Specimens',
                groupIcon: 'biotech',
                groupKeys: [] // Dynamically populated
            },
            {
                groupName: 'Performers',
                groupIcon: 'medical_information',
                groupKeys: [] // Dynamically populated
            },
            {
                groupName: 'Conclusion',
                groupIcon: 'summarize',
                groupKeys: ['conclusion']
            }
        ]
    };

    override ngOnInit(): void {
        // Compute permissions for 'diagnosticReport' resource
        this.computePermissions('diagnosticReport');

        // Set columns dynamically based on patient context
        const patientId = this.stateService.currentPatientId$.getValue();
        if (patientId) {
            // Patient-specific view - no need for patient column
            this.displayedColumns = ['issued', 'status', 'category', 'code', 'actions'];
        } else {
            // Org-wide view - include patient column
            this.displayedColumns = ['issued', 'status', 'category', 'code', 'subject', 'actions'];
        }

        // Load diagnostic reports
        this.loadDiagnosticReports();
    }

    private loadDiagnosticReports(): void {
        const patientId = this.stateService.currentPatientId$.getValue();

        // Determine data source based on patient context
        const diagnosticReports$ = patientId
            ? this.stateService.PatientResources.diagnosticReports
            : this.stateService.orgWideResources.diagnosticReports;

        const currentReports = diagnosticReports$.getValue();

        if (!currentReports || currentReports.length === 0) {
            // Fetch from backend if not in state
            console.log('Diagnostic reports not found in state, fetching from backend...');

            const fetchUrl = patientId
                ? `${this.backendUrl}/DiagnosticReport?subject=Patient/${patientId}&_count=1000`
                : `${this.backendUrl}/DiagnosticReport?_count=1000`;

            this.http.get<Bundle<DiagnosticReport>>(fetchUrl)
                .pipe(
                    switchMap((bundle) => {
                        console.log(`Fetched ${bundle.entry?.length || 0} diagnostic reports from backend`);

                        // Persist to state
                        if (!patientId) {
                            this.stateService.processOrgWideBundleTransaction(bundle);
                        } else {
                            this.stateService.processBundleTransaction(bundle);
                        }

                        // Extract reports
                        const reports = (bundle.entry || [])
                            .map(e => ({ actualResource: e.resource as DiagnosticReport }))
                            .filter(r => r.actualResource);

                        return this.loadAndFlattenRelatedResources(reports);
                    }),
                    catchError((err) => {
                        console.error('Failed to load diagnostic reports:', err);
                        this.errorService.openandCloseError('Failed to load diagnostic reports');
                        return of([]);
                    })
                )
                .subscribe((flattenedReports) => {
                    this.initiateDataSourceSubscription(of(flattenedReports));
                    this.applyFiltersAndTransforms();
                });
        } else {
            // Use existing data
            console.log('Using existing diagnostic reports from state');

            this.loadAndFlattenRelatedResources(currentReports)
                .subscribe((flattenedReports) => {
                    this.initiateDataSourceSubscription(of(flattenedReports));
                    this.applyFiltersAndTransforms();
                });
        }
    }

    /**
     * Load all related resources (Observations, Specimens, Practitioners) and flatten data
     */
    private loadAndFlattenRelatedResources(
        reports: Array<{ actualResource: DiagnosticReport }>
    ): Observable<Array<{ actualResource: DiagnosticReport & Record<string, any> }>> {
        if (!reports.length) return of([]);

        // Extract all referenced resource IDs
        const observationIds = this.extractResultIds(reports);
        const specimenIds = this.extractSpecimenIds(reports);
        const performerIds = this.extractPerformerIds(reports);

        // Fetch all related resources in parallel
        return forkJoin({
            observations: this.fetchObservations(observationIds),
            specimens: this.fetchSpecimens(specimenIds),
            performers: this.fetchPerformers(performerIds)
        }).pipe(
            map(({ observations, specimens, performers }) => {
                // Flatten and merge related data into each report
                return reports.map(report => ({
                    actualResource: this.flattenReportData(
                        report.actualResource,
                        observations,
                        specimens,
                        performers
                    )
                }));
            })
        );
    }

    /**
     * Extract observation IDs from result references
     */
    private extractResultIds(reports: Array<{ actualResource: DiagnosticReport }>): string[] {
        const ids = new Set<string>();
        reports.forEach(report => {
            const results = report.actualResource.result || [];
            results.forEach(ref => {
                const id = this.extractIdFromReference(ref.reference);
                if (id) ids.add(id);
            });
        });
        return Array.from(ids);
    }

    /**
     * Extract specimen IDs from specimen references
     */
    private extractSpecimenIds(reports: Array<{ actualResource: DiagnosticReport }>): string[] {
        const ids = new Set<string>();
        reports.forEach(report => {
            const specimens = report.actualResource.specimen || [];
            specimens.forEach(ref => {
                const id = this.extractIdFromReference(ref.reference);
                if (id) ids.add(id);
            });
        });
        return Array.from(ids);
    }

    /**
     * Extract performer IDs from performer references
     */
    private extractPerformerIds(reports: Array<{ actualResource: DiagnosticReport }>): string[] {
        const ids = new Set<string>();
        reports.forEach(report => {
            const performers = report.actualResource.performer || [];
            performers.forEach(ref => {
                const id = this.extractIdFromReference(ref.reference);
                if (id && ref.reference?.startsWith('Practitioner/')) ids.add(id);
            });
        });
        return Array.from(ids);
    }

    /**
     * Extract ID from FHIR reference string
     */
    private extractIdFromReference(ref?: string): string | null {
        if (!ref) return null;
        const parts = ref.split('/');
        return parts.length > 1 ? parts.pop() || null : null;
    }

    /**
     * Fetch observations by IDs
     */
    private fetchObservations(ids: string[]): Observable<Observation[]> {
        if (!ids.length) return of([]);

        const idParams = ids.join(',');
        return this.http.get<Bundle<Observation>>(`${this.backendUrl}/Observation?_id=${idParams}`).pipe(
            map(bundle => (bundle.entry || []).map(e => e.resource as Observation).filter(Boolean)),
            catchError(err => {
                console.error('Failed to fetch observations:', err);
                return of([]);
            })
        );
    }

    /**
     * Fetch specimens by IDs
     */
    private fetchSpecimens(ids: string[]): Observable<Specimen[]> {
        if (!ids.length) return of([]);

        const idParams = ids.join(',');
        return this.http.get<Bundle<Specimen>>(`${this.backendUrl}/Specimen?_id=${idParams}`).pipe(
            map(bundle => (bundle.entry || []).map(e => e.resource as Specimen).filter(Boolean)),
            catchError(err => {
                console.error('Failed to fetch specimens:', err);
                return of([]);
            })
        );
    }

    /**
     * Fetch practitioners by IDs
     */
    private fetchPerformers(ids: string[]): Observable<Practitioner[]> {
        if (!ids.length) return of([]);

        const idParams = ids.join(',');
        return this.http.get<Bundle<Practitioner>>(`${this.backendUrl}/Practitioner?_id=${idParams}`).pipe(
            map(bundle => (bundle.entry || []).map(e => e.resource as Practitioner).filter(Boolean)),
            catchError(err => {
                console.error('Failed to fetch practitioners:', err);
                return of([]);
            })
        );
    }

    /**
     * Flatten report data with related resources
     */
    private flattenReportData(
        report: DiagnosticReport,
        observations: Observation[],
        specimens: Specimen[],
        performers: Practitioner[]
    ): DiagnosticReport & Record<string, any> {
        const flattened: any = { ...report };

        // Flatten results (observations)
        const resultRefs = report.result || [];
        resultRefs.forEach((ref, index) => {
            const obsId = this.extractIdFromReference(ref.reference);
            const obs = observations.find(o => o.id === obsId);

            if (obs) {
                const n = index + 1;
                flattened[`result ${n} - code`] = obs.code?.text || obs.code?.coding?.[0]?.display || 'N/A';

                // Extract value based on type
                if ((obs as any).valueQuantity) {
                    const vq = (obs as any).valueQuantity;
                    flattened[`result ${n} - value`] = `${vq.value || ''} ${vq.unit || ''}`.trim() || 'N/A';
                } else if ((obs as any).valueString) {
                    flattened[`result ${n} - value`] = (obs as any).valueString;
                } else if ((obs as any).valueCodeableConcept) {
                    const vcc = (obs as any).valueCodeableConcept;
                    flattened[`result ${n} - value`] = vcc.text || vcc.coding?.[0]?.display || 'N/A';
                } else {
                    flattened[`result ${n} - value`] = 'N/A';
                }

                flattened[`result ${n} - status`] = obs.status || 'N/A';
                if (obs.effectiveDateTime) {
                    flattened[`result ${n} - date`] = new Date(obs.effectiveDateTime).toLocaleString();
                }
            }
        });

        // Flatten specimens
        const specimenRefs = report.specimen || [];
        specimenRefs.forEach((ref, index) => {
            const specId = this.extractIdFromReference(ref.reference);
            const spec = specimens.find(s => s.id === specId);

            if (spec) {
                const n = index + 1;
                flattened[`specimen ${n} - type`] = spec.type?.text || spec.type?.coding?.[0]?.display || 'N/A';

                if (spec.collection?.collectedDateTime) {
                    flattened[`specimen ${n} - collected`] = new Date(spec.collection.collectedDateTime).toLocaleString();
                }

                if (spec.container?.[0]) {
                    flattened[`specimen ${n} - container`] = spec.container[0].type?.text ||
                        spec.container[0].type?.coding?.[0]?.display || 'N/A';
                }
            }
        });

        // Flatten performers
        const performerRefs = report.performer || [];
        performerRefs.forEach((ref, index) => {
            const perfId = this.extractIdFromReference(ref.reference);
            const perf = performers.find(p => p.id === perfId);

            if (perf) {
                const n = index + 1;
                const name = perf.name?.[0];
                if (name) {
                    const fullName = [
                        ...(name.prefix || []),
                        ...(name.given || []),
                        name.family
                    ].filter(Boolean).join(' ');
                    flattened[`performer ${n} - name`] = fullName || 'N/A';
                } else {
                    flattened[`performer ${n} - name`] = ref.display || 'N/A';
                }

                if (perf.qualification?.[0]) {
                    flattened[`performer ${n} - qualification`] =
                        perf.qualification[0].code?.text ||
                        perf.qualification[0].code?.coding?.[0]?.display || 'N/A';
                }
            }
        });

        // Add display fields
        flattened['code'] = report.code?.text || report.code?.coding?.[0]?.display || 'N/A';
        flattened['category'] = report.category?.[0]?.coding?.[0]?.code ||
            report.category?.[0]?.coding?.[0]?.display || 'N/A';

        if (report.issued) {
            flattened['issued'] = new Date(report.issued).toLocaleString();
        }

        return flattened;
    }

    /**
     * Apply filters and transforms
     */
    private applyFiltersAndTransforms(): void {
        // Filter out entered-in-error
        this.applyFilter('validity', (row) => row['status'] !== 'entered-in-error');

        // Sort by issued date (descending - newest first)
        this.sortData(['issued'], false);

        // Transform data for display
        this.transformData((row) => ({
            ...row,
            issuedDisplay: row['issued'] ? new Date(row['issued']).toLocaleDateString() : 'N/A',
            categoryDisplay: row['category']?.[0]?.coding?.[0]?.display || row['category']?.[0]?.coding?.[0]?.code || 'N/A'
        }));

        // Organize search filters
        this.organizingSearchFilters(this.diagnosticReportFilters);
    }

    /**
     * Show diagnostic report details
     */
    showRow(report: DiagnosticReport & Record<string, any>): void {
        this.showDiagnosticReportDetails(report);
    }

    /**
     * Show diagnostic report details dialog
     */
    showDiagnosticReportDetails(report: DiagnosticReport & Record<string, any>): void {
        // Build dynamic groups based on available data
        const builder = { ...this.diagnosticReportDetailsBuilder };

        // Populate Results group keys
        const resultKeys = Object.keys(report).filter(k => k.startsWith('result '));
        if (resultKeys.length > 0) {
            const resultsGroup = builder.groups.find(g => g.groupName === 'Results');
            if (resultsGroup) resultsGroup.groupKeys = resultKeys;
        }

        // Populate Specimens group keys
        const specimenKeys = Object.keys(report).filter(k => k.startsWith('specimen '));
        if (specimenKeys.length > 0) {
            const specimensGroup = builder.groups.find(g => g.groupName === 'Specimens');
            if (specimensGroup) specimensGroup.groupKeys = specimenKeys;
        }

        // Populate Performers group keys
        const performerKeys = Object.keys(report).filter(k => k.startsWith('performer '));
        if (performerKeys.length > 0) {
            const performersGroup = builder.groups.find(g => g.groupName === 'Performers');
            if (performersGroup) performersGroup.groupKeys = performerKeys;
        }

        const dialogRef = this.dialog.open(DetailzViewzComponent, {
            maxHeight: '93vh',
            maxWidth: '650px',
            width: '100%',
            data: {
                resourceData: report,
                detailsBuilderObject: builder,
                actionButtons: this.buildDiagnosticReportActions(report)
            }
        });

        const sub = dialogRef.componentInstance?.actionInvoked.subscribe(({ key }) => {
            this.handleDetailAction(key, report);
        });

        dialogRef.afterClosed().subscribe(() => sub?.unsubscribe());
    }

    /**
     * Build action buttons for diagnostic report details
     */
    private buildDiagnosticReportActions(report: DiagnosticReport & Record<string, any>): DetailActionButton[] {
        const actions: DetailActionButton[] = [];

        // View full report action
        actions.push({
            key: 'viewFullReport',
            label: 'View Full Report',
            icon: 'description',
            capabilities: [{ resource: 'diagnosticReport', action: 'read' }]
        });

        // Amend report action (only for final reports)
        if (report.status === 'final') {
            actions.push({
                key: 'amendReport',
                label: 'Amend Report',
                icon: 'edit_note',
                capabilities: [{ resource: 'diagnosticReport', action: 'update' }]
            });
        }

        return actions;
    }

    /**
     * Handle action button clicks
     */
    private handleDetailAction(actionKey: string, report: DiagnosticReport & Record<string, any>): void {
        switch (actionKey) {
            case 'viewFullReport':
                if (!report?.id) {
                    this.errorService.openandCloseError('DiagnosticReport id is missing');
                    return;
                }

                this.dialog.open(DiagnosticReportDetailsComponent, {
                    maxHeight: '93vh',
                    maxWidth: '100%',
                    width: '1100px',
                    data: { diagnosticReportId: report.id }
                });
                break;

            case 'amendReport':
                console.log('Amend report:', report);
                // TODO: Open amend report dialog
                break;

            default:
                console.warn('Unknown action key:', actionKey);
                break;
        }
    }

    override ngOnDestroy(): void {
        console.log('Cleaning up diagnostic reports');
        super.ngOnDestroy?.();
    }
    onExportReports() {
        console.log('Exporting encounters');
    }
}
